/* ─────────────────────────────────────────────────────────────
   BOOKING ROW — the behaviour behind snippets/booking-row.liquid
───────────────────────────────────────────────────────────────

   Companion to snippets/booking-row.liquid and assets/booking-row.css. The Liquid
   draws one line in a booking, the CSS sizes it, and this file makes its controls
   work. All three belong to every surface that lists booking items: the cart page,
   the cart drawer, the product page confirmation panel and the booking bar summary.

   ── WHY IT IS ITS OWN FILE ──────────────────────────────────────────────────────
   Every function here was moved out of pb-cart.js unchanged, in one block, on
   09-09-2026. It was working code — the quantity rules, the greying at limits, the
   handling of Shopify's 422 refusal, the guard that stopped the cart reload loop —
   and it only ever ran on the cart page, because that is the only page pb-cart.js
   loads on.

   So the shared Liquid row rendered a stepper and a Remove button on the product
   page that did nothing at all. Moving the behaviour rather than reimplementing it
   means the four surfaces cannot disagree about what a press does: Remove in the
   drawer goes through the same code as Remove in the cart.

   Nothing was retyped. Four points were rewritten, and only those four, all for the
   same reason — the file no longer knows which surface it is on:

     1. PB_CART_SECTIONS, the cart page's two section ids, became bkrSectionIds(),
        read from window.bookingRowSections at call time.
     2. A second registry, window.bookingRowPanels, for surfaces that are not
        sections and refresh through ?section_id= instead.
     3. pbApplySections and pbRefreshSections no longer reload when there is nothing
        to swap. On a surface with no registered sections that is the normal case.
     4. /cart/change.js is sent without a sections key when there are none.

   ── HOW A SURFACE REGISTERS ─────────────────────────────────────────────────────
   Before or after this file loads, either is fine — both registries are read when
   they are used, not when this file runs:

     <script>
       window.bookingRowSections = (window.bookingRowSections || [])
         .concat(['pb-cart', 'main-cart']);
     </script>

     <script>
       (window.bookingRowPanels = window.bookingRowPanels || []).push({
         section: 'booking-panel',
         into: '.pp-booking-card-booked-info',
         visibleClass: 'is-visible',
         url: '/products/some-handle'      // optional; '/' if omitted
       });
     </script>

   ── EVENTS ──────────────────────────────────────────────────────────────────────
   `bookingrow:updated` on document after any successful change, whichever surface
   it came from. `pbcart:updated` still fires as well, for the cart page's own
   ledger and calendar, so nothing that listened for it had to change.

   ── THE FUNCTION NAMES ARE STILL pb* ────────────────────────────────────────────
   Deliberate, for the same reason the CSS class names are still the cart page's:
   renaming is a second change stacked on a move, and it would make this file
   impossible to diff against the one it came out of. The pb prefix now means
   "booking", not "the cart page".
*/

(function () {
    'use strict';

    /* ══ Cart edits without a page reload ══════════════════════════════════════
       Every qty change and every remove used to end in location.reload(). A full
       document load to change a number from 2 to 3 — new HTML, all scripts re-parsed,
       scroll position lost — is why the cart feels broken even when it is working.

       Two changes make it feel instant:

       1. The number on screen updates BEFORE the request goes out. The customer's tap
          is acknowledged immediately; the server confirms a moment later.
       2. The response re-renders only the cart sections, via Shopify's Section
          Rendering API, and we swap that HTML in. No navigation.

       Section ids come from templates/cart.json, which renders BOTH carts — pb-cart
       (ours) and main-cart (the original Vision cart, still on the page below it). Both
       must be refreshed or the two will disagree about what is in the cart.

       If anything goes wrong we fall back to location.reload(). Slow, but correct — and
       correct matters more here than fast, because this is the page that takes money. */
    /* Was a const list of the cart page's two section ids. It is now a registry each
       surface fills, because the same code runs on four of them and only the cart page
       has sections called pb-cart and main-cart.

       Read at CALL time, never at load time, so a surface can register before or after
       this file loads and the order cannot matter. */
    function bkrSectionIds() {
        const ids = window.bookingRowSections;
        if (!Array.isArray(ids)) return [];

        /*  ── #103: ONLY ASK FOR SECTIONS THAT ARE ACTUALLY ON THE PAGE ────────────
            Found 12-09-2026 from a console error on every date change:

              [booking-row] No element on the page for section(s): main-cart

            `main-cart` is registered below because templates/cart.json renders both
            carts — ours and the original Vision one beneath it. It is now **disabled**
            in that template. A disabled section renders nothing and emits no wrapper, so
            the id resolves to no element, and every swap requested it, failed to place
            it, and logged.

            Not breakage: pb-cart still applied, so `applied` was non-zero and the reload
            fallback never fired. But it is noise in precisely the place a REAL miss would
            appear, and the whole reason that error exists is that silence let a broken
            swap masquerade as a working one for two days (see the note above). An error
            that cries wolf on every interaction is worse than no error.

            Filtering here rather than editing the registration, because the registration
            is right: the page MAY have both, and if somebody re-enables main-cart it
            should start being swapped again with no code change. What is wrong is asking
            the server for markup we have nowhere to put.

            The filter also shrinks the request — ?sections=pb-cart rather than
            ?sections=pb-cart,main-cart — so Shopify stops rendering a section we discard. */
        /*  ── 365: ASK FOR THE SECTION BY ITS FULL ID (Ben, 11-10-2026) ─────────────
            "The cart loads black, then changes to blue once any change to the cart is
            made." The refresh asked for ?sections=pb-cart — the section's FILE name.
            Shopify renders a section asked for by file name as a standalone section with
            its SCHEMA DEFAULTS, not the settings saved in templates/cart.json. So every
            refresh quietly reset the panel to defaults: the head went blue (the default),
            and any edited cart copy would have reverted the same way.
            The fix asks for the id the section actually has on the page
            (template--…__pb-cart), which Shopify renders with the saved settings. The
            response comes back under that same id, which bkrSectionEl already finds. */
        return ids.filter(id => id && bkrSectionEl(id)).map(id => {
            const el = bkrSectionEl(id);
            return (el && el.id) ? el.id.replace(/^shopify-section-/, '') : id;
        });
    }

    /* Surfaces that are not sections — the PDP confirmation panel, the cart drawer.
       Each entry is { section, into, visibleClass }: a section FILE rendered on its own
       through ?section_id=, dropped into a container by selector. See bkrRefreshPanels
       at the foot of this file. */
    function bkrPanels() {
        const p = window.bookingRowPanels;
        return Array.isArray(p) ? p : [];
    }

    /*  ── THE SECTION SWAP NEVER WORKED, AND THE FALLBACK HID IT (10-09-2026) ────
        A section that lives in a JSON template does not render with the id you wrote in
        that template. `templates/cart.json` names our section "pb-cart", and Shopify
        renders it as

            <div id="shopify-section-template--16376756338790__pb-cart">

        so `getElementById('shopify-section-pb-cart')` found nothing, every swap applied
        zero sections, and pbApplySections fell through to its own last resort:
        location.reload().

        That is why a date change took five to seven seconds. Not the requests — they
        finished in 1.8s — but a full page load, with every third-party script, fired
        deliberately by our own fallback the moment the swap "failed". The trace from the
        cart read: dispatchBookingBarChanged → pb-cart.js → pbRefreshSections →
        pbApplySections → gone.

        Worse, it was invisible: a reload lands on a page showing the same correct cart,
        so "cart edits no longer reload the page" was believed for two days. **A fallback
        that produces the right answer slowly will hide a broken fast path indefinitely.
        Log loudly when one fires.**

        THE FIX IS ON THE LOOKUP SIDE ONLY. The REQUEST side is fine and stays as it
        is: ?sections=pb-cart,main-cart returned 200 with both sections, keyed by exactly
        those names — the probe caught the response. It is the element lookup that has to
        cope with the rendered id, so the resolver below matches either the exact id or
        any id ending in __<name>.

        Deliberately not "fixed" by registering {{ section.id }} from the Liquid instead.
        The Section Rendering API keys its response by the name you asked for, and the
        short template key is the name that is known to work; swapping it for the long
        rendered id to fix a lookup problem would be a change to the half that already
        works.  */
    function bkrSectionEl(id, root) {
        const doc = root || document;
        return doc.getElementById('shopify-section-' + id)
            || doc.querySelector('[id^="shopify-section-"][id$="__' + id + '"]');
    }

    function bkrNotify() {
        document.dispatchEvent(new CustomEvent('bookingrow:updated'));
    }

    function pbApplySections(sections) {
        /* No registered sections means this surface has none to swap — the product page
           asks for its panel instead. Reloading there would throw the customer off the
           page they are still shopping on. */
        if (!bkrSectionIds().length) {
            return bkrRefreshPanels().then(() => { bkrNotify(); return true; });
        }
        if (!sections) { location.reload(); return false; }

        let applied = 0;
        const missed = [];

        Object.keys(sections).forEach(id => {
            const target = bkrSectionEl(id);
            const html = sections[id];
            if (!target || typeof html !== 'string') { missed.push(id); return; }

            /* Shopify returns the section wrapper too, so parse and take its insides —
               replacing the wrapper itself would drop its classes and its id. The
               returned wrapper carries the FULL id even when a short name was requested,
               which is the same trap as above. */
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fresh = bkrSectionEl(id, parsed);
            /*  dev-build 403 (Ben 12-10-2026): replacing the whole cart section collapses the
                page for a frame, so the browser pulled the scroll back up. Hold the old
                height during the swap and put the scroll back where it was. */
            const y = window.scrollY, h = target.offsetHeight;
            target.style.minHeight = h + 'px';
            target.innerHTML = fresh ? fresh.innerHTML : html;
            if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
            requestAnimationFrame(() => { target.style.minHeight = ''; if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y); });
            applied++;
        });

        if (missed.length) {
            console.error('[booking-row] No element on the page for section(s):', missed.join(', '),
                '— found:', [...document.querySelectorAll('[id^="shopify-section-"]')].map(el => el.id).join(', '));
        }

        if (!applied) {
            /* Last resort, and it says so now. Silence here is what let a broken swap
               masquerade as a working one for two days. */
            console.error('[booking-row] Nothing could be swapped — falling back to a full page reload.');
            location.reload();
            return false;
        }

        /* The swapped-in HTML has no listeners of its own. Everything in this file is
           delegated from document so it keeps working, but anything that ran once at
           load — the ledger, the calendar — needs telling. */
        document.dispatchEvent(new CustomEvent('pbcart:updated'));

        /* Sections and panels are different mechanisms and both can be on one page: the
           cart page swaps sections, and the drawer over it is a panel. */
        bkrRefreshPanels();
        bkrNotify();
        return true;
    }

    /* Re-render the cart sections WITHOUT changing the cart. Used after a date change,
       where the line items have already been re-priced and the attributes written — the
       cart is correct, the HTML on screen just isn't. */
    function pbRefreshSections() {
        if (!bkrSectionIds().length) return bkrRefreshPanels().then(bkrNotify);

        return fetch(window.location.pathname + '?sections=' + bkrSectionIds().join(','))
            .then(r => r.json())
            .then(sections => pbApplySections(sections))
            .catch(err => {
                console.error('Cart refresh failed, falling back to reload:', err);
                location.reload();
            });
    }

    /* The step, floor and ceiling for one line, read off the row.

       Defaults match pp-product.js exactly — 1 / 1 / 999. If a product has no rules the
       cart behaves as it always did. */
    function pbQtyRules(row) {
        const d = (row && row.dataset) || {};

        /* Two sources of truth, and they are not ours to reconcile away.

           custom.qty_* is how WE sell the product — uplights in fours.
           variant.quantity_rule.* is what SHOPIFY will accept, and it wins at the API
           no matter what we render.

           Take the stricter of each. Showing a ceiling the platform will refuse is how
           you get a button that appears to work and silently does something else. */
        const ourStep = parseInt(d.qtyStep, 10) || 1;
        const ourMin  = parseInt(d.qtyMin, 10)  || 1;
        const ourMax  = parseInt(d.qtyMax, 10)  || 999;

        const shopMax = parseInt(d.shopifyMax, 10);
        const shopMin = parseInt(d.shopifyMin, 10);
        const shopInc = parseInt(d.shopifyIncrement, 10);

        return {
            step: Number.isFinite(shopInc) && shopInc > ourStep ? shopInc : ourStep,
            min:  Number.isFinite(shopMin) && shopMin > ourMin  ? shopMin : ourMin,
            max:  Number.isFinite(shopMax) && shopMax < ourMax  ? shopMax : ourMax
        };
    }

    /* ── Say why the button did nothing ─────────────────────────────────────────
       A button that refuses silently reads as a broken button. The customer presses
       minus on 4, nothing moves, and the reasonable conclusion is that the site is
       stuck — so they press it again.

       Deliberately a note, not an error: they have not done anything wrong, they have
       reached the edge of what we hire. Wording comes from the section settings so it
       stays editable, with {count} standing in for the number.

       It replaces itself rather than stacking, and clears on the next successful
       change so a stale sentence never sits under a quantity it no longer describes. */
    function pbQtyNote(row, text) {
        if (!row || !text) return;

        const qtyBox = row.querySelector('.bc-detail-body-gear-item-qty');
        if (!qtyBox) return;

        let note = row.querySelector('.bc-qty-note');
        if (!note) {
            note = document.createElement('p');
            note.className = 'bc-qty-note';
            /* Announced by screen readers when it appears, without stealing focus. */
            note.setAttribute('role', 'status');
            qtyBox.insertAdjacentElement('afterend', note);
        }

        note.textContent = text;
        note.classList.add('is-visible');
    }

    function pbClearQtyNote(row) {
        const note = row && row.querySelector('.bc-qty-note');
        if (note) note.classList.remove('is-visible');
    }

    /* ── Grey out what cannot be pressed ────────────────────────────────────────
       Mirrors the product page, which disables +/- at the bounds rather than letting
       the press fail. A disabled control is a permanent statement of the limit; a
       message is a fleeting one that vanishes on the next render.

       Class name is is-disable, matching .pp-bcm-qty-min/-plus on the PDP so the two
       quantity controls look identical. (The cart uses is-disabl-ED elsewhere, on the
       checkout button. Two conventions in one theme is untidy and worth a tidy pass,
       but the quantity control should match the other quantity control.)

       The disabled attribute goes on as well as the class: the PDP only greys the
       button and relies on its handler returning early, which leaves a control that
       looks dead but still takes clicks and is announced as available. */
    function pbSyncQtyButtons(row) {
        if (!row) return;

        const qtyEl = row.querySelector('.bc-detail-body-gear-item-qty-value');
        const minBtn = row.querySelector('.bc-detail-body-gear-item-qty-min');
        const plusBtn = row.querySelector('.bc-detail-body-gear-item-qty-plus');
        if (!qtyEl) return;

        const current = parseInt(qtyEl.textContent.trim(), 10);
        if (!Number.isFinite(current)) return;

        const rules = pbQtyRules(row);
        const atFloor = current - rules.step < rules.min;
        const atCeiling = current + rules.step > rules.max;

        if (minBtn) {
            minBtn.classList.toggle('is-disable', atFloor);
            minBtn.disabled = atFloor;
        }
        if (plusBtn) {
            plusBtn.classList.toggle('is-disable', atCeiling);
            plusBtn.disabled = atCeiling;
        }
    }

    function pbSyncAllQtyButtons() {
        document.querySelectorAll('.bc-detail-body-gear-item').forEach(pbSyncQtyButtons);
    }

    /* Run on load, and again after every section swap — the re-rendered HTML comes back
       with fresh buttons that know nothing about their own limits. */
    document.addEventListener('DOMContentLoaded', pbSyncAllQtyButtons);
    document.addEventListener('pbcart:updated', pbSyncAllQtyButtons);
    pbSyncAllQtyButtons();

    /*  530 (Ben 20-10-2026): the greying only ran on page load and after a cart-page swap,
        so rows drawn later — the drawer (every open and refresh), "Already added" on the
        product page, rows re-rendered after a change — kept live − / + buttons at their
        limits, and a tap showed a skeleton for nothing. Any gear row that appears or is
        re-drawn anywhere is now synced as it lands. */
    window.pbSyncAllQtyButtons = pbSyncAllQtyButtons;
    let bkrSyncQueued = false;
    new MutationObserver(function (muts) {
        if (bkrSyncQueued) return;
        for (const m of muts) {
            for (const n of m.addedNodes) {
                if (n.nodeType === 1 && (n.matches('.bc-detail-body-gear-item, .bc-detail-body-gear-item-qty-value') || n.querySelector('.bc-detail-body-gear-item'))) {
                    bkrSyncQueued = true;
                    requestAnimationFrame(function () { bkrSyncQueued = false; pbSyncAllQtyButtons(); });
                    return;
                }
            }
        }
    }).observe(document.documentElement, { childList: true, subtree: true });

    function pbCartChange(key, quantity, row) {
        pbClearQtyNote(row);
        if (row) row.classList.add('is-updating');

        /* Only ask for sections when there are some. An empty `sections` string is not
           the same as omitting it — Shopify answers with no sections key at all, which
           the 200 branch below would read as a failed swap and reload the page. */
        const changeBody = { id: key, quantity: quantity };
        if (bkrSectionIds().length) {
            changeBody.sections = bkrSectionIds().join(',');
            changeBody.sections_url = window.location.pathname;
        }

        return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(changeBody)
        })
        .then(async r => {
            /* ── Shopify said no ────────────────────────────────────────────────
               /cart/change.js answers 422 when it cannot honour the quantity, with a
               plain-English reason in `description`:

                 "Only 100 items were added to your cart due to availability."

               This chain used to be .then(r => r.json()) with no r.ok check. A 422 body
               has no `items` and no `sections`, so pbApplySections received undefined
               and fell through to its location.reload() fallback. The page reloaded, the
               quantity snapped back, and the reason was discarded — which is precisely
               what "it maxes out at 100 and says nothing" looked like from the outside.

               Shopify usually applies as much as it can before refusing, so the cart on
               the server is now some third number. Re-read it rather than trusting
               either what we asked for or what we last drew. */
            if (!r.ok) {
                const err = await r.json().catch(() => null);

                const fresh = await fetch('/cart.js').then(x => x.json()).catch(() => null);
                const line = fresh && Array.isArray(fresh.items)
                    ? fresh.items.find(i => i.key === key)
                    : null;

                if (row) {
                    if (line && typeof line.quantity === 'number') {
                        /* Our wording, Shopify's number. Their copy says "added to your
                           cart", which is wrong for a quantity change and says "cart"
                           where we say booking. */
                        const copy = row.dataset.qtyStockNote
                            || '{count} is all we have free for these dates';
                        pbQtyNote(row, copy.replace('{count}', line.quantity));
                    } else if (err && err.description) {
                        /* No line to read a number off — show Shopify's sentence rather
                           than nothing. Rare, and better than a silent failure. */
                        pbQtyNote(row, err.description);
                    }
                }

                /* Re-render from the server's actual state, so the number on screen is
                   true even though the request was refused. */
                return pbRefreshSections();
            }

            const cart = await r.json();
            /* ── Did we get what we asked for? ──────────────────────────────────
               Belt and braces, for the case where Shopify honours the request partially
               and still answers 200. The 422 path above covers outright refusal; this
               covers a quiet trim.

               Either way the number shown comes from the server, so the note stays
               honest whatever the underlying reason turns out to be. */
            if (cart && Array.isArray(cart.items) && row) {
                const line = cart.items.find(i => i.key === key);
                if (line && typeof line.quantity === 'number' && line.quantity !== quantity) {
                    const copy = row.dataset.qtyStockNote
                        || '{count} is all we have free for these dates';
                    pbQtyNote(row, copy.replace('{count}', line.quantity));
                }
            }
            return pbApplySections(cart && cart.sections);
        })
        .catch(err => {
            console.error('Cart update failed, falling back to reload:', err);
            location.reload();
        });
    }

    /* ── Gear item qty and remove ────────────────────────────────
    Handles qty +/- and remove for cart line items          */
    document.addEventListener('click', function (e) {

        // ── Remove ─────────────────────────────────────────────
        const removeBtn = e.target.closest('.bc-detail-body-gear-item-remove');
        if (removeBtn) {
            const key = removeBtn.dataset.key;
            const row = removeBtn.closest('.bc-detail-body-gear-item');

            /* Fade the row out rather than removing it. If the request fails the
               reload puts it back, and a row that vanished and returned is more
               alarming than one that dimmed. */
            if (row) row.classList.add('is-removing');

            pbCartChange(key, 0, row);
            return;
        }

        // ── Qty minus ──────────────────────────────────────────
        const minBtn = e.target.closest('.bc-detail-body-gear-item-qty-min');
        if (minBtn) {
            const key     = minBtn.dataset.key;
            const row     = minBtn.closest('.bc-detail-body-gear-item');
            const qtyEl   = minBtn.closest('.bc-detail-body-gear-item-qty').querySelector('.bc-detail-body-gear-item-qty-value');
            const current = parseInt(qtyEl.textContent.trim());
            const rules   = pbQtyRules(row);
            const next    = current - rules.step;

            /* Below the floor is not a smaller order, it is no order — but removing a
               line on a minus press would be a nasty surprise. Refuse instead; Remove
               is right there. */
            /* Belt and braces. pbSyncQtyButtons has already disabled this button, so
               reaching here means the sync had not run — a keyboard press before load,
               or markup without the data attributes. Refuse quietly rather than
               sending a quantity we know will be rejected. */
            if (next < rules.min) return;

            qtyEl.textContent = next;   // acknowledge the tap immediately
            pbSyncQtyButtons(row);      // and grey the button if that was the last step
            pbCartChange(key, next, row);
            return;
        }

        // ── Qty plus ───────────────────────────────────────────
        const plusBtn = e.target.closest('.bc-detail-body-gear-item-qty-plus');
        if (plusBtn) {
            const key     = plusBtn.dataset.key;
            const qtyEl   = plusBtn.closest('.bc-detail-body-gear-item-qty').querySelector('.bc-detail-body-gear-item-qty-value');
            const current = parseInt(qtyEl.textContent.trim());

            const rowPlus = plusBtn.closest('.bc-detail-body-gear-item');
            const rulesP  = pbQtyRules(rowPlus);
            const nextP   = current + rulesP.step;

            if (nextP > rulesP.max) return;   // see the note on the minus branch

            qtyEl.textContent = nextP;   // acknowledge the tap immediately
            pbSyncQtyButtons(rowPlus);   // and grey the button if that was the last step
            pbCartChange(key, nextP, rowPlus);
            return;
        }
    });

    /* ── Pick new dates button ───────────────────────────────────
    Opens the booking bar panel and scrolls to top          */
    document.addEventListener('click', function (e) {
        if (e.target.closest('.bc-detail-body-gear-item-booked-pick-new-date')) {
            const panel = document.querySelector('.bb-panel');
            if (panel) panel.classList.remove('hidden');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    });


    /* ── PANELS ──────────────────────────────────────────────────────────────────
       A panel is a surface that lists booking rows but is not a section, so the
       Section Rendering API cannot swap it: the product page's confirmation panel
       lives inside the booking card, and the drawer lives in the layout.

       Both are refreshed by asking Shopify to render one section FILE on its own —
       /?section_id=booking-panel — and dropping its insides into a container. The
       cart object is global, so it does not matter that the request is made against
       the home page; the rows come back correct.

       Server-rendered rather than built in memory, which is the point of the whole
       exercise: the panel survives navigation, and its rows are the same Liquid the
       cart page uses instead of a second implementation that drifts from it. */
    function bkrRefreshPanels() {
        const panels = bkrPanels();
        if (!panels.length) return Promise.resolve();

        return Promise.all(panels.map(panel => {
            const into = panel.into ? document.querySelector(panel.into) : null;
            if (!into || !panel.section) return null;

            /* Rendered against the panel's own URL, not '/'. The product page's panel
               lists one product's lines, and a section can only know which product that
               is by inheriting the product page's context — Liquid cannot read a query
               string. Defaults to '/' for a panel that wants the whole cart. */
            const base = panel.url || '/';
            const join = base.indexOf('?') === -1 ? '?' : '&';

            return fetch(base + join + 'section_id=' + encodeURIComponent(panel.section))
                .then(r => r.text())
                .then(html => {
                    const parsed = new DOMParser().parseFromString(html, 'text/html');
                    /* Shopify wraps the section in a div of its own, which is not wanted
                       inside a card, so take the marked element's insides. Falling back
                       to the body keeps the panel rendering if the marker is dropped. */
                    const src = parsed.querySelector('[data-booking-panel]');
                    into.innerHTML = src ? src.innerHTML : parsed.body.innerHTML;

                    /* An empty cart renders an empty panel. Hiding it is the
                       container's job, not the section's, because the container is what
                       carries the confirmation styling. */
                    if (panel.visibleClass) {
                        const hasRows = !!into.querySelector('.bc-detail-body-gear-item');
                        into.classList.toggle(panel.visibleClass, hasRows);
                    }

                    /* Fresh buttons know nothing about their own limits. */
                    pbSyncAllQtyButtons();
                })
                .catch(err => console.error('Booking panel refresh failed:', err));
        }));
    }

    /* ── WHAT OTHER FILES MAY CALL ───────────────────────────────────────────────
       pb-cart.js calls refreshSections() after a date change, where the lines have
       already been re-priced and only the HTML is stale. pp-product.js calls
       refreshPanels() after an add. Nothing else is public: the stepper, Remove and
       "Pick new dates" are delegated from document and need no help. */
    window.BookingRow = {
        refreshSections: pbRefreshSections,
        refreshPanels: bkrRefreshPanels,
        applySections: pbApplySections,
        change: pbCartChange,
        syncQtyButtons: pbSyncAllQtyButtons
    };
})();